import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import {
	CallActivityPerformanceFragment,
	CustomerSatisfactionStoreFragment,
	LongevityFragment,
	TopPerformerBonusFragment,
	UserFullNameFragment,
	BonusDefinitionFragment,
	CommissionTiersFragment,
	MetricFragment,
	InputValueFragment,
	VariableOperationsGpsBonusSetupFragment,
} from "@PayPlans/api/fragments/commons";

import {
	CashAndLeaseBumpFragment,
	CITBonusesFragment,
	DefaultCommissionsFragment,
	DepartmentOverrideNetIncomeFragment,
	MaxPayoutFragment,
	ProductBonusesFragment,
	PVRQualifierFragment,
} from "@PayPlans/api/fragments/managers/finance";

// Add new finance pay plans
export async function createFinancePayPlan({ locationId, position }) {
	const query = gql`
		mutation createFinancePayPlan($locationId: String!, $position: String!) {
			createFinancePayPlan(locationId: $locationId, position: $position) {
				name
				createdAt
				formatVersion
			}
		}
	`;

	const variables = { locationId, position };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.createFinancePayPlan || null;
	} catch (error) {
		return error;
	}
}

export async function updateFinanceAvailablePayPlan({ locationId, information, position }) {
	const { _id, updatedBy, available } = information;
	const query = gql`
		mutation updateFinanceAvailablePayPlan($locationId: String!, $input: UpdateAvailablePayPlanInput!, $position: String!) {
			updateFinanceAvailablePayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				available
			}
		}
	`;

	const variables = {
		locationId,
		position,
		input: {
			_id,
			updatedBy,
			available,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateFinanceAvailablePayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Add new finance pay plans
export async function cloneFinancePayPlan({ locationId, information, actualPosition }) {
	const { _id, createdBy, position } = information;
	const query = gql`
		mutation CloneFinancePayPlan($locationId: String!, $input: ClonePayPlan!, $position: String!) {
			cloneFinancePayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				backendPVRComputation
				default {
					...FinanceDefaultCommissions
				}
				productBonuses {
					...ProductBonuses
				}
				cashAndLeaseBump {
					...CashAndLeaseBump
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				cit {
					...CITBonuses
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				departmentOverrideNetIncome {
					...DepartmentOverrideNetIncome
				}
				pvrQualifier {
					...PVRQualifier
				}
				topSalesBonus {
					...TopPerformerBonus
				}
				maxPayout {
					...MaxPayout
				}
				longevity {
					...Longevity
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					...UserFullName
				}
				updatedAt
				updatedBy {
					...UserFullName
				}
			}
		}
		${DefaultCommissionsFragment}
		${ProductBonusesFragment}
		${CashAndLeaseBumpFragment}
		${CallActivityPerformanceFragment}
		${CITBonusesFragment}
		${CustomerSatisfactionStoreFragment}
		${DepartmentOverrideNetIncomeFragment}
		${PVRQualifierFragment}
		${TopPerformerBonusFragment}
		${MaxPayoutFragment}
		${LongevityFragment}
		${UserFullNameFragment}
		${BonusDefinitionFragment}
		${CommissionTiersFragment}
		${MetricFragment}
		${InputValueFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		locationId,
		position: actualPosition,
		input: {
			payPlanId: _id,
			createdBy,
			position,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.cloneFinancePayPlan || null;
	} catch (error) {
		return error;
	}
}

// Delete selected finance pay plans
export async function deleteFinancePayPlan({ locationId, _id, position }) {
	const query = gql`
		mutation deleteFinancePayPlan($locationId: String!, $id: String!, $position: String!) {
			deleteFinancePayPlan(id: $id, locationId: $locationId, position: $position) {
				_id
				locationId
			}
		}
	`;

	const variables = {
		locationId,
		position,
		id: _id,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteFinancePayPlan || null;
	} catch (error) {
		return error;
	}
}

// Update selected finance pay plan
export async function updateFinancePayPlan({ locationId, payPlan, position }) {
	const query = gql`
		mutation updateFinancePayPlan($locationId: String!, $input: UpdateFinancePayPlanInput!, $position: String!) {
			updateFinancePayPlan(input: $input, locationId: $locationId, position: $position) {
				_id
				locationId
				name
				position
				backendPVRComputation
				default {
					...FinanceDefaultCommissions
				}
				productBonuses {
					...ProductBonuses
				}
				cashAndLeaseBump {
					...CashAndLeaseBump
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				cit {
					...CITBonuses
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				departmentOverrideNetIncome {
					...DepartmentOverrideNetIncome
				}
				pvrQualifier {
					...PVRQualifier
				}
				topSalesBonus {
					...TopPerformerBonus
				}
				maxPayout {
					...MaxPayout
				}
				longevity {
					...Longevity
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					...UserFullName
				}
				updatedAt
				updatedBy {
					...UserFullName
				}
			}
		}
		${DefaultCommissionsFragment}
		${ProductBonusesFragment}
		${CashAndLeaseBumpFragment}
		${CallActivityPerformanceFragment}
		${CITBonusesFragment}
		${CustomerSatisfactionStoreFragment}
		${DepartmentOverrideNetIncomeFragment}
		${PVRQualifierFragment}
		${TopPerformerBonusFragment}
		${MaxPayoutFragment}
		${LongevityFragment}
		${UserFullNameFragment}
		${BonusDefinitionFragment}
		${CommissionTiersFragment}
		${MetricFragment}
		${InputValueFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		locationId,
		position,
		input: payPlan,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateFinancePayPlan || null;
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Delete selected finance pay plan
export async function saveNewFinancePayPlan({ locationId, payPlan, position }) {
	const query = gql`
		mutation saveNewFinancePayPlan($locationId: String!, $input: SaveFinancePayPlan!, $position: String!) {
			saveNewFinancePayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				backendPVRComputation
				default {
					...FinanceDefaultCommissions
				}
				productBonuses {
					...ProductBonuses
				}
				cashAndLeaseBump {
					...CashAndLeaseBump
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				cit {
					...CITBonuses
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				departmentOverrideNetIncome {
					...DepartmentOverrideNetIncome
				}
				pvrQualifier {
					...PVRQualifier
				}
				topSalesBonus {
					...TopPerformerBonus
				}
				maxPayout {
					...MaxPayout
				}
				longevity {
					...Longevity
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					...UserFullName
				}
				updatedAt
				updatedBy {
					...UserFullName
				}
			}
		}
		${DefaultCommissionsFragment}
		${ProductBonusesFragment}
		${CashAndLeaseBumpFragment}
		${CallActivityPerformanceFragment}
		${CITBonusesFragment}
		${CustomerSatisfactionStoreFragment}
		${DepartmentOverrideNetIncomeFragment}
		${PVRQualifierFragment}
		${TopPerformerBonusFragment}
		${MaxPayoutFragment}
		${LongevityFragment}
		${UserFullNameFragment}
		${BonusDefinitionFragment}
		${CommissionTiersFragment}
		${MetricFragment}
		${InputValueFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		locationId,
		position,
		input: payPlan,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.saveNewFinancePayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}
